import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Briefcase, Scale, Stethoscope, Laptop, GraduationCap,
  Home, Calculator, Palette, Dumbbell, Scissors, User,
  IndianRupee, Store, ChevronRight, Layers
} from 'lucide-react';

const ICON_MAP = {
  briefcase:     Briefcase,
  Briefcase:     Briefcase,
  scale:         Scale,
  stethoscope:   Stethoscope,
  laptop:        Laptop,
  graduationcap: GraduationCap,
  GraduationCap: GraduationCap,
  home:          Home,
  calculator:    Calculator,
  palette:       Palette,
  dumbbell:      Dumbbell,
  scissors:      Scissors,
  User:          User,
  user:          User,
  IndianRupee:   IndianRupee,
  indianrupee:   IndianRupee,
  Store:         Store,
  store:         Store,
};

const COLORS = [
  { bg: 'bg-amber-50',   text: 'text-amber-600',   active: 'border-amber-400 bg-amber-50',   hover: 'hover:border-amber-200 hover:bg-amber-50/50' },
  { bg: 'bg-blue-50',    text: 'text-blue-600',    active: 'border-blue-400 bg-blue-50',    hover: 'hover:border-blue-200 hover:bg-blue-50/50' },
  { bg: 'bg-emerald-50', text: 'text-emerald-600', active: 'border-emerald-400 bg-emerald-50', hover: 'hover:border-emerald-200 hover:bg-emerald-50/50' },
  { bg: 'bg-purple-50',  text: 'text-purple-600',  active: 'border-purple-400 bg-purple-50',  hover: 'hover:border-purple-200 hover:bg-purple-50/50' },
  { bg: 'bg-rose-50',    text: 'text-rose-600',    active: 'border-rose-400 bg-rose-50',    hover: 'hover:border-rose-200 hover:bg-rose-50/50' },
  { bg: 'bg-cyan-50',    text: 'text-cyan-600',    active: 'border-cyan-400 bg-cyan-50',    hover: 'hover:border-cyan-200 hover:bg-cyan-50/50' },
  { bg: 'bg-orange-50',  text: 'text-orange-600',  active: 'border-orange-400 bg-orange-50',  hover: 'hover:border-orange-200 hover:bg-orange-50/50' },
  { bg: 'bg-indigo-50',  text: 'text-indigo-600',  active: 'border-indigo-400 bg-indigo-50',  hover: 'hover:border-indigo-200 hover:bg-indigo-50/50' },
  { bg: 'bg-teal-50',    text: 'text-teal-600',    active: 'border-teal-400 bg-teal-50',    hover: 'hover:border-teal-200 hover:bg-teal-50/50' },
  { bg: 'bg-pink-50',    text: 'text-pink-600',    active: 'border-pink-400 bg-pink-50',    hover: 'hover:border-pink-200 hover:bg-pink-50/50' },
];

/**
 * Reusable right-column categories sidebar for shop pages.
 * Props:
 *   categories      – full list from API
 *   currentSlug     – active category slug (highlight it)
 *   title           – optional title override
 */
const ShopSidebar = ({ categories = [], currentSlug = '', title = 'Browse Categories' }) => {
  const navigate = useNavigate();

  // Only show categories that have at least 1 template
  const visible = categories.filter(c => (c.template_count || 0) > 0);

  if (!visible.length) return null;

  return (
    <aside className="w-full">
      {/* Header */}
      <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-100">
        <Layers className="w-4 h-4 text-indigo-500" />
        <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">{title}</span>
      </div>

      <nav className="flex flex-col gap-1.5">
        {visible.map((cat, idx) => {
          const color = COLORS[idx % COLORS.length];
          const IconComp = ICON_MAP[cat.icon] || Briefcase;
          const isActive = cat.slug === currentSlug;

          return (
            <button
              key={cat.id}
              onClick={() => navigate(`/shop/${cat.slug}`)}
              className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl border transition-all duration-150 text-left group
                ${isActive
                  ? `${color.active} border-opacity-80 shadow-sm`
                  : `border-gray-100 bg-white ${color.hover}`
                }`}
            >
              {/* Icon */}
              <div className={`w-8 h-8 rounded-lg flex-shrink-0 flex items-center justify-center ${color.bg} transition-colors`}>
                <IconComp className={`${color.text}`} size={16} />
              </div>
              {/* Label */}
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium truncate ${ isActive ? 'text-gray-900' : 'text-gray-700' }`}>
                  {cat.name}
                </p>
                <p className="text-[10px] text-gray-400">
                  {cat.template_count || 0} design{(cat.template_count || 0) !== 1 ? 's' : ''}
                </p>
              </div>
              {/* Arrow */}
              <ChevronRight
                className={`w-3.5 h-3.5 flex-shrink-0 transition-all ${
                  isActive ? color.text : 'text-gray-300 group-hover:text-gray-400 group-hover:translate-x-0.5'
                }`}
              />
            </button>
          );
        })}
      </nav>
    </aside>
  );
};

export default ShopSidebar;
